import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../components/PageHeader.js';
import { useTenantContext } from '../api/tenant-context.js';
import { listCampaigns, type OutboundCampaign } from '../api/campaigns.js';
import { listChannels, type Channel } from '../api/channels.js';
import {
  archiveInboundCampaign,
  createInboundCampaign,
  listInboundCampaigns,
  updateInboundCampaign,
  type InboundCampaign,
  type InboundCampaignPatch,
  type InboundTriggerType,
  type InboundCampaignStatus,
} from '../api/inbound-campaigns.js';

type Tab = 'inbound' | 'outbound';

export function Campanas(): JSX.Element {
  const [tab, setTab] = useState<Tab>('inbound');
  const { tenant } = useTenantContext();
  const tenantId = tenant?.id ?? '';

  // BUG P1 #6 QA: contadores en tabs deben reflejar el real count desde
  // el mount; antes el outboundQ solo se ejecutaba al activar el tab,
  // mostrando 0 hasta el primer click. Ambos queries activos siempre.
  const inboundQ = useQuery({
    queryKey: ['inbound-campaigns', tenantId],
    queryFn: () => listInboundCampaigns(tenantId),
    enabled: !!tenantId,
  });
  const outboundQ = useQuery({
    queryKey: ['campaigns', tenantId],
    queryFn: () => listCampaigns(tenantId),
    enabled: !!tenantId,
  });
  const channelsQ = useQuery({
    queryKey: ['channels', tenantId],
    queryFn: () => listChannels(tenantId),
    enabled: !!tenantId,
  });

  const qc = useQueryClient();
  const refreshInbound = (): void => {
    void qc.invalidateQueries({ queryKey: ['inbound-campaigns', tenantId] });
  };

  return (
    <>
      <PageHeader
        title="Campañas"
        subtitle="Campañas inbound (mensajes entrantes que disparan flujos) y outbound (envío proactivo de templates)."
      />

      <div className="flex gap-1 mb-5" role="tablist">
        <button
          type="button"
          className={`tab ${tab === 'inbound' ? 'active' : ''}`}
          onClick={() => setTab('inbound')}
        >
          Inbound{' '}
          <span className="ml-1 text-ink-4">
            {(inboundQ.data ?? []).length}
          </span>
        </button>
        <button
          type="button"
          className={`tab ${tab === 'outbound' ? 'active' : ''}`}
          onClick={() => setTab('outbound')}
        >
          Outbound{' '}
          <span className="ml-1 text-ink-4">{(outboundQ.data ?? []).length}</span>
        </button>
      </div>

      {tab === 'inbound' && (
        <InboundPanel
          tenantId={tenantId}
          items={inboundQ.data ?? []}
          loading={inboundQ.isLoading}
          channels={(channelsQ.data ?? []).filter((c) => c.deletedAt === null)}
          onChanged={refreshInbound}
        />
      )}
      {tab === 'outbound' && (
        <OutboundTable items={outboundQ.data ?? []} loading={outboundQ.isLoading} />
      )}
    </>
  );
}

// ── Inbound ────────────────────────────────────────────────────────────────

function InboundPanel({
  tenantId,
  items,
  loading,
  channels,
  onChanged,
}: {
  tenantId: string;
  items: InboundCampaign[];
  loading: boolean;
  channels: Channel[];
  onChanged: () => void;
}): JSX.Element {
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [confirmArchive, setConfirmArchive] = useState<InboundCampaign | null>(null);

  const channelById = new Map(channels.map((c) => [c.id, c]));

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button type="button" onClick={() => setCreating((v) => !v)} className="btn-primary">
          {creating ? 'Cancelar' : '+ Nueva campaña inbound'}
        </button>
      </div>

      {creating && (
        <InboundForm
          tenantId={tenantId}
          mode="create"
          channels={channels}
          onSuccess={() => {
            setCreating(false);
            onChanged();
          }}
          onCancel={() => setCreating(false)}
        />
      )}

      {loading && <div className="panel text-ink-3 text-sm">Cargando…</div>}
      {!loading && items.length === 0 && !creating && (
        <div className="panel text-ink-3 text-sm">
          Sin campañas inbound. Creá una para que los mensajes entrantes de un
          canal disparen un flujo.
        </div>
      )}

      {items.map((c) =>
        editing === c.id ? (
          <InboundForm
            key={c.id}
            tenantId={tenantId}
            mode="edit"
            initial={c}
            channels={channels}
            onSuccess={() => {
              setEditing(null);
              onChanged();
            }}
            onCancel={() => setEditing(null)}
          />
        ) : (
          <InboundCard
            key={c.id}
            camp={c}
            channelName={channelById.get(c.channelId)?.name ?? c.channelId}
            onEdit={() => setEditing(c.id)}
            onArchive={() => setConfirmArchive(c)}
          />
        ),
      )}

      {confirmArchive && (
        <ConfirmModal
          title="¿Archivar campaña inbound?"
          desc={`Se archivará "${confirmArchive.name}". Los mensajes entrantes ya no la dispararán.`}
          confirmLabel="Archivar"
          onConfirm={async () => {
            await archiveInboundCampaign(tenantId, confirmArchive.id);
            setConfirmArchive(null);
            onChanged();
          }}
          onCancel={() => setConfirmArchive(null)}
        />
      )}
    </div>
  );
}

const STATUS_TONE: Record<InboundCampaignStatus, string> = {
  active: 'badge-ok',
  paused: 'badge-warn',
  archived: 'badge-neutral',
};

function InboundCard({
  camp,
  channelName,
  onEdit,
  onArchive,
}: {
  camp: InboundCampaign;
  channelName: string;
  onEdit: () => void;
  onArchive: () => void;
}): JSX.Element {
  return (
    <div className="panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[14px] font-semibold">{camp.name}</div>
          <div className="text-[12px] text-ink-3 mt-0.5">
            {camp.description ?? '—'}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`badge ${STATUS_TONE[camp.status]}`}>{camp.status}</span>
          <span className="badge badge-neutral">prio {camp.priority}</span>
          <button type="button" onClick={onEdit} className="btn-mini">Editar</button>
          <button type="button" onClick={onArchive} className="btn-mini btn-mini-danger">Archivar</button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 text-[12.5px]">
        <DefRow label="Canal" value={channelName} />
        <DefRow label="Flow ID" value={camp.flowId} mono />
        <DefRow label="Trigger" value={camp.triggerType} />
        <DefRow
          label="Keywords"
          value={
            camp.triggerType === 'keyword'
              ? (camp.keywords ?? []).map((k) => `"${k}"`).join(', ') || '—'
              : '—'
          }
          mono
        />
        <DefRow label="Conversaciones" value={String(camp.conversationsStarted)} mono />
        <DefRow label="Versión" value={`v${camp.version}`} />
      </div>
    </div>
  );
}

function InboundForm({
  tenantId,
  mode,
  initial,
  channels,
  onSuccess,
  onCancel,
}: {
  tenantId: string;
  mode: 'create' | 'edit';
  initial?: InboundCampaign;
  channels: Channel[];
  onSuccess: () => void;
  onCancel: () => void;
}): JSX.Element {
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [channelId, setChannelId] = useState(initial?.channelId ?? channels[0]?.id ?? '');
  const [flowId, setFlowId] = useState(initial?.flowId ?? '');
  const [triggerType, setTriggerType] = useState<InboundTriggerType>(
    initial?.triggerType ?? 'always',
  );
  const [keywords, setKeywords] = useState((initial?.keywords ?? []).join(', '));
  const [welcomeMessage, setWelcomeMessage] = useState(initial?.welcomeMessage ?? '');
  const [status, setStatus] = useState<InboundCampaignStatus>(initial?.status ?? 'active');
  const [priority, setPriority] = useState<number>(initial?.priority ?? 50);
  const [err, setErr] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: async () => {
      if (!name.trim()) throw new Error('Nombre es obligatorio');
      if (!channelId) throw new Error('Seleccioná un channel');
      if (!flowId.trim() || !/^[a-z][a-z0-9-]*$/.test(flowId.trim())) {
        throw new Error('flowId inválido (kebab-case, sólo a-z, 0-9 y guion)');
      }
      const kwArr = keywords
        .split(',')
        .map((k) => k.trim())
        .filter(Boolean);
      if (triggerType === 'keyword' && kwArr.length === 0) {
        throw new Error('Con trigger=keyword debés especificar al menos 1 keyword');
      }
      const payload = {
        name: name.trim(),
        ...(description.trim() ? { description: description.trim() } : {}),
        channelId,
        flowId: flowId.trim(),
        triggerType,
        ...(triggerType === 'keyword' ? { keywords: kwArr } : {}),
        ...(welcomeMessage.trim() ? { welcomeMessage: welcomeMessage.trim() } : {}),
        status,
        priority,
      };
      if (mode === 'create') {
        return createInboundCampaign(tenantId, payload);
      }
      if (initial) {
        const patch: InboundCampaignPatch = payload;
        return updateInboundCampaign(tenantId, initial.id, patch, initial.version);
      }
      throw new Error('invalid state');
    },
    onSuccess: () => onSuccess(),
    onError: (e: unknown) => setErr(e instanceof Error ? e.message : 'error'),
  });

  return (
    <div className="panel space-y-3">
      <div className="text-[13px] font-semibold">
        {mode === 'create' ? 'Nueva campaña inbound' : `Editar ${initial?.name}`}
      </div>
      <Field label="Nombre">
        <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="input" maxLength={120} />
      </Field>
      <Field label="Descripción (opcional)">
        <textarea
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={500}
          className="input"
        />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Channel" helper="Mensajes entrantes de este canal serán evaluados.">
          <select
            value={channelId}
            onChange={(e) => setChannelId(e.target.value)}
            className="input"
          >
            <option value="">Seleccionar…</option>
            {channels.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} · {c.channelType}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Flow ID" helper="Kebab-case. Debe existir un flow publicado.">
          <input
            type="text"
            value={flowId}
            onChange={(e) => setFlowId(e.target.value)}
            placeholder="welcome-wa"
            className="input mono"
          />
        </Field>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Field label="Trigger">
          <select
            value={triggerType}
            onChange={(e) => setTriggerType(e.target.value as InboundTriggerType)}
            className="input"
          >
            <option value="always">always</option>
            <option value="keyword">keyword</option>
            <option value="first_message">first_message</option>
          </select>
        </Field>
        <Field label="Status">
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as InboundCampaignStatus)}
            className="input"
          >
            <option value="active">active</option>
            <option value="paused">paused</option>
          </select>
        </Field>
        <Field label="Prioridad (0-100)">
          <input
            type="number"
            min={0}
            max={100}
            value={priority}
            onChange={(e) => setPriority(Number(e.target.value))}
            className="input mono"
          />
        </Field>
      </div>

      {triggerType === 'keyword' && (
        <Field label="Keywords (separadas por coma)" helper="Hasta 20 keywords. Match case-insensitive.">
          <input
            type="text"
            value={keywords}
            onChange={(e) => setKeywords(e.target.value)}
            placeholder="hola, inicio, agente"
            className="input mono"
          />
        </Field>
      )}

      <Field label="Mensaje de bienvenida (opcional)" helper="Se envía antes de iniciar el flow.">
        <textarea
          rows={2}
          value={welcomeMessage}
          onChange={(e) => setWelcomeMessage(e.target.value)}
          maxLength={1024}
          className="input"
        />
      </Field>

      {err && <p className="text-[12px] text-err">{err}</p>}
      <div className="flex justify-end gap-2 pt-2">
        <button type="button" onClick={onCancel} className="btn-ghost">Cancelar</button>
        <button type="button" onClick={() => mut.mutate()} disabled={mut.isPending} className="btn-primary">
          {mut.isPending ? '…' : mode === 'create' ? 'Crear campaña' : 'Guardar cambios'}
        </button>
      </div>
    </div>
  );
}

// ── Outbound (tabla read-only del backend existente) ──────────────────────

function OutboundTable({ items, loading }: { items: OutboundCampaign[]; loading: boolean }): JSX.Element {
  if (loading) return <div className="panel text-ink-3 text-sm">Cargando…</div>;
  if (items.length === 0) return <div className="panel text-ink-3 text-sm">No hay campañas outbound.</div>;
  return (
    <div className="panel" style={{ padding: 0 }}>
      <table className="w-full text-[13px]">
        <thead>
          <tr className="text-left text-[11.5px] text-ink-4">
            <th className="px-4 py-2.5 font-medium" style={{ background: 'var(--bg-2)' }}>Nombre</th>
            <th className="px-4 py-2.5 font-medium" style={{ background: 'var(--bg-2)' }}>Estado</th>
            <th className="px-4 py-2.5 font-medium" style={{ background: 'var(--bg-2)' }}>Contactos</th>
            <th className="px-4 py-2.5 font-medium" style={{ background: 'var(--bg-2)' }}>Entregados</th>
            <th className="px-4 py-2.5 font-medium" style={{ background: 'var(--bg-2)' }}>Fallidos</th>
            <th className="px-4 py-2.5 font-medium" style={{ background: 'var(--bg-2)' }}>Programada</th>
          </tr>
        </thead>
        <tbody>
          {items.map((c) => (
            <tr key={c.id} className="border-t border-border">
              <td className="px-4 py-3 text-ink-1">{c.name}</td>
              <td className="px-4 py-3"><span className="badge badge-neutral">{c.status}</span></td>
              <td className="px-4 py-3 mono">{c.contactsCount}</td>
              <td className="px-4 py-3 mono text-ok">{c.deliveredCount}</td>
              <td className="px-4 py-3 mono text-err">{c.failedCount}</td>
              <td className="px-4 py-3 text-ink-3">{c.scheduledAt ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Shared ────────────────────────────────────────────────────────────────

function ConfirmModal({
  title, desc, confirmLabel, onConfirm, onCancel,
}: {
  title: string; desc: string; confirmLabel: string;
  onConfirm: () => void | Promise<void>; onCancel: () => void;
}): JSX.Element {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="w-full max-w-md rounded-lg border border-border-2 bg-surface p-5">
        <div className="text-[15px] font-semibold mb-2">{title}</div>
        <p className="text-[13px] text-ink-3 mb-4">{desc}</p>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="btn-ghost">Cancelar</button>
          <button
            type="button"
            onClick={() => void onConfirm()}
            className="btn-primary"
            style={{ background: 'var(--err)' }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, helper, children }: { label: string; helper?: string; children: React.ReactNode }): JSX.Element {
  return (
    <label className="block">
      <span className="block mb-1 text-[12px] text-ink-3">{label}</span>
      {children}
      {helper && <span className="block mt-1 text-[10.5px] text-ink-4">{helper}</span>}
    </label>
  );
}

function DefRow({ label, value, mono }: { label: string; value: string; mono?: boolean }): JSX.Element {
  return (
    <div className="flex gap-3 py-1 border-b border-border last:border-b-0">
      <div className="text-[11px] text-ink-4 w-[110px] shrink-0">{label}</div>
      <div className={`text-[12.5px] ${mono ? 'mono' : ''} text-ink-2 break-all`}>{value}</div>
    </div>
  );
}
